import { secretProviderFor } from "@p0security/integrations-vaulted-credential-shared/locator";

// Every secret name here is built in the canonical slash-delimited form and
// rewritten for Google Secret Manager, where `/` becomes `_` and `.` becomes
// `-`. Item ids hold no `_`, so the `_` after a site's id ends its segment:
// a prefix for one site can't match another's secrets.

const { replacer } = secretProviderFor("gcp");

// Google Secret Manager's rule for a secret's ID, which the connector also
// holds a secret's name to.
const GCP_SECRET_NAME_PATTERN = /^[A-Za-z0-9_-]{1,255}$/;

/** The error for a name that Google Secret Manager wouldn't take. It doesn't
 * repeat the name, which may be the secret itself, pasted in its place. */
export const INVALID_GCP_SECRET_NAME =
  "That isn't a secret's name in Google Secret Manager. Enter the name alone, without its project or a version: letters, digits, underscores and hyphens, up to 255 characters.";

/** Whether `name`, as it stands, can name a secret in Google Secret Manager. */
export const isGcpSecretName = (name: unknown): name is string =>
  typeof name === "string" && GCP_SECRET_NAME_PATTERN.test(name);

/** The secret that holds a pool account's API key, as its whole value, by
 * the site's and the pool account's item ids. The customer creates it, and
 * only the connector reads it. */
export const toPoolSecretName = (args: {
  poolAccount: string;
  site: string;
}) => {
  const { poolAccount, site } = args;
  return replacer(`p0/install/jira-ai/${site}/${poolAccount}/api-key`);
};

/** The prefix of every pool account's secret on the site `site`, which the
 * connector is granted read on. */
export const toPoolSecretPrefix = (site: string) =>
  replacer(`p0/install/jira-ai/${site}/`);

/** The prefix of every request's token secret on the site `site`, which the
 * connector creates, writes and deletes. */
export const toTokenSecretPrefix = (site: string) =>
  replacer(`p0/access-token/jira-ai/${site}/`);

/** The token secret of one request, by its site and its `tokenId`. */
export const toTokenSecretName = (args: { site: string; tokenId: string }) => {
  const { site, tokenId } = args;
  return `${toTokenSecretPrefix(site)}${replacer(tokenId)}`;
};
